import React, { useMemo } from 'react';
import {
	Bar,
	BarChart,
	CartesianGrid,
	LabelList,
	Legend,
	ResponsiveContainer,
	Text,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';
import {
	CargoUnitTypes,
	PortActionTypeLabels,
	PortActionTypes,
} from '@shared/utils/constants';
import { formatNumber } from '@shared/utils/formatNumber';
import { formatQuantity } from '@shared/utils/string';
import type { LocalPortRotationWithKey } from '@client/screens/estimates/details/context/hooks/usePortRotationHandlers';

const BAR_HEIGHT = 50;
const AXIS_AND_LEGEND_HEIGHT = 80;
const LABEL_GAP = 4;
const MAX_PORT_NAME_LENGTH = 16;

const formatSegmentLabel = (value: number) => (
	value ? formatNumber(value, { separateThousands: true }, 0) : ''
);

const truncatePortName = (portName: string) => (
	portName.length > MAX_PORT_NAME_LENGTH ?
		`${portName.slice(0, MAX_PORT_NAME_LENGTH - 1)}…` :
		portName
);

const IntakeCalculatorBars = ({
	portRotation,
}: {
	portRotation: LocalPortRotationWithKey[];
}) => {
	const data = useMemo(() => portRotation.map((entry) => {
		const cargoQuantity = (
			entry.type === PortActionTypes.LOADING ?
				entry.cargoOnboardOnDeparture :
				entry.cargoOnboardOnArrival
		) ?? 0;

		return {
			portName: entry.port?.name,
			action: entry.type === PortActionTypes.LOADING || entry.type === PortActionTypes.DISCHARGING ?
				PortActionTypeLabels[entry.type] :
				null,
			portMax: entry.portMax,
			cargoQuantity,
			spaceAvailable: entry.portMax != null ? Math.max(0, entry.portMax - cargoQuantity) : 0,
			bunkerReduction: entry.bunkerReduction ?? 0,
			constantsReduction: entry.constantsReduction ?? 0,
			draftReduction: entry.draftReduction ?? 0,
		};
	}), [portRotation]);

	const renderPortTick = ({ x, y, payload, index }: {
		x: number;
		y: number;
		payload: { value: string };
		index: number;
	}) => {
		const action = data[index]?.action;

		return (
			<g transform={`rotate(-45, ${x - 30}, ${y})`}>
				<title>{payload.value}</title>
				<Text x={x} y={action ? y - LABEL_GAP : y} textAnchor="end" verticalAnchor={action ? 'end' : 'middle'} fill="#666">
					{truncatePortName(payload.value ?? '')}
				</Text>
				{action && (
					<Text x={x} y={y + LABEL_GAP} textAnchor="end" verticalAnchor="start" fill="#666" fontSize={12}>
						{action}
					</Text>
				)}
			</g>
		);
	};

	if (portRotation.length === 0) {
		return null;
	}

	return (
		<ResponsiveContainer width="100%" height={data.length * BAR_HEIGHT + AXIS_AND_LEGEND_HEIGHT}>
			<BarChart
				layout="vertical"
				data={data}
				margin={{
					top: 20,
					right: 0,
					left: 0,
					bottom: 5,
				}}
			>
				<Legend />
				<CartesianGrid />
				<XAxis
					type="number"
					tickFormatter={(value: number) => formatNumber(value, { separateThousands: true }, 0)}
				/>
				<YAxis type="category" dataKey="portName" width={120} tick={renderPortTick} />
				<Tooltip
					formatter={(value: number) => formatQuantity(value, CargoUnitTypes.MT)}
					labelFormatter={(label: string, payload) => {
						const portMax = payload?.[0]?.payload?.portMax;

						return portMax != null ?
							`${label} - Port max intake: ${formatQuantity(portMax, CargoUnitTypes.MT)}` :
							label;
					}}
				/>
				<Bar name="Cargo quantity" dataKey="cargoQuantity" stackId="intake" fill="#1677ff">
					<LabelList dataKey="cargoQuantity" position="center" fill="#fff" formatter={formatSegmentLabel} />
				</Bar>
				<Bar name="Space available for cargo" dataKey="spaceAvailable" stackId="intake" fill="#13c2c2">
					<LabelList dataKey="spaceAvailable" position="center" fill="#fff" formatter={formatSegmentLabel} />
				</Bar>
				<Bar name="Reduction for bunkers" dataKey="bunkerReduction" stackId="intake" fill="#f5222d">
					<LabelList dataKey="bunkerReduction" position="center" fill="#fff" formatter={formatSegmentLabel} />
				</Bar>
				<Bar name="Reduction for constants" dataKey="constantsReduction" stackId="intake" fill="#722ed1">
					<LabelList dataKey="constantsReduction" position="center" fill="#fff" formatter={formatSegmentLabel} />
				</Bar>
				<Bar name="Reduction for draft" dataKey="draftReduction" stackId="intake" fill="#fa8c16">
					<LabelList dataKey="draftReduction" position="center" fill="#fff" formatter={formatSegmentLabel} />
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};

export default IntakeCalculatorBars;
